import { Popover, PopoverBody } from "reactstrap";
import { useEffect, useRef, useState } from "react";

import { HexColorPicker } from "react-colorful";
import { THEME } from "../../../../constants";
import chroma from "chroma-js";

const FORMATS = ["hex", "rgb", "hsl"];

// reactstrap hands Popper `boundary: 'scrollParent'`, a Popper v1 value that v2
// does not know, so its own preventOverflow never fires. Declaring the modifier
// here replaces it, PopperContent drops its default once the name is taken. The
// altAxis is the part that matters: on a right-placed popover the main axis is
// horizontal, so without it nothing stops the popover from running off the
// bottom of the window and stretching the page into a second scrollbar.
const POPPER_MODIFIERS = [
  { name: "preventOverflow", options: { altAxis: true, padding: 8 } },
];

// The check drawn on the selected swatch has to survive both a pale yellow and
// a dark blue, so it flips with the swatch's own luminance.
const contrastClass = (hex) =>
  chroma(hex).luminance() > 0.4 ? "on-light" : "on-dark";

// Only the display needs a case: chroma parses all three formats on the way
// in, so a rgb() pasted while the field reads hex is still understood.
export const formatColor = (color, format) => {
  const c = chroma(color);

  if (format === "rgb") return `rgb(${c.rgb().join(", ")})`;

  if (format === "hsl") {
    const [h, s, l] = c.hsl();
    // A pure grey has no hue and chroma reports it as NaN, which would print
    // as "NaN" in the field. The N/A colour #A9A9A9 is grey, so this happens.
    return `hsl(${Math.round(h || 0)}, ${Math.round(s * 100)}%, ${Math.round(
      l * 100
    )}%)`;
  }

  return c.hex();
};

// One popover for both ways of picking. The free picker is the main surface,
// and the palette underneath is the shortcut: its ten entries are the ones
// useAttributeColor already hands out, the set that stays readable side by
// side on the map. Nothing hands the user off to the browser's own picker.
export const ColorPicker = ({ color, disabled, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [format, setFormat] = useState("hex");
  // Text being typed. Null means "derive it from the colour", which is what
  // every other control in the popover feeds. Blur clears it, and clicking any
  // of those controls blurs the field, so nothing has to reset it by hand.
  const [draft, setDraft] = useState(null);
  const target = useRef(null);
  // The colour being dragged, kept here and sent once the picker rests for a
  // moment: it fires on every move, and each onChange is a store update. Not
  // onChangeEnd: on a quick click it fires before the move lands, which left
  // the picked colour stuck here, unsaved and hiding the typed ones.
  const [live, setLive] = useState(null);
  const shown = live ?? color;

  useEffect(() => {
    if (live === null) return undefined;
    const timer = setTimeout(() => {
      onChange(live);
      setLive(null);
    }, 150);
    return () => clearTimeout(timer);
  }, [live, onChange]);

  // The popover is portalled to the body, so scrolling the attribute list slides
  // its row away underneath it: it ends up hanging over the map, and low in the
  // list it stretches the page into a second scrollbar. Scrolling on its own is
  // fine though, the popover follows its row, so the trigger going out of sight
  // is what closes it. An observer with no root still accounts for the clipping
  // of every ancestor, which is precisely the scrolling list here.
  useEffect(() => {
    if (!isOpen) return undefined;

    const observer = new IntersectionObserver(([row]) => {
      if (!row.isIntersecting) setIsOpen(false);
    });
    observer.observe(target.current);
    return () => observer.disconnect();
  }, [isOpen]);

  return (
    <>
      <button
        type="button"
        ref={target}
        className="color-swatch"
        style={{ backgroundColor: shown }}
        disabled={disabled}
        aria-label={`Change color, currently ${shown}`}
      />
      <Popover
        isOpen={isOpen}
        target={target}
        toggle={() => setIsOpen(!isOpen)}
        trigger="legacy"
        placement="right"
        modifiers={POPPER_MODIFIERS}
        // Absolutely positioned, the popover counts towards the body's scroll
        // height and briefly grows the page a scrollbar before Popper has
        // placed it. Fixed takes it out of that measurement entirely, the same
        // reason selects in a scrollable drawer use menuPosition="fixed".
        strategy="fixed"
      >
        <PopoverBody className="color-picker">
          <HexColorPicker color={shown} onChange={setLive} />
          <div className="color-picker-value">
            <span className="color-swatch" style={{ backgroundColor: shown }} />
            <div className="color-picker-formats">
              {FORMATS.map((name) => (
                <button
                  type="button"
                  key={name}
                  className={`color-picker-format ${
                    format === name ? "is-active" : ""
                  }`}
                  onClick={() => setFormat(name)}
                >
                  {name.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          <input
            className="form-control form-control-sm color-picker-input"
            aria-label="Color value"
            value={draft ?? formatColor(shown, format)}
            onChange={(e) => {
              setDraft(e.target.value);
              if (chroma.valid(e.target.value)) {
                // Wins over a drag still waiting to be sent.
                setLive(null);
                onChange(chroma(e.target.value).hex());
              }
            }}
            onBlur={() => setDraft(null)}
          />
          <div className="color-picker-palette">
            <span className="color-picker-label">Site palette</span>
            <div className="color-picker-grid">
              {THEME.categoricalPalette.map((swatch) => {
                const isActive = swatch.toLowerCase() === color?.toLowerCase();

                return (
                  <button
                    type="button"
                    key={swatch}
                    title={swatch}
                    style={{ backgroundColor: swatch }}
                    className={`color-swatch ${
                      isActive ? `active ${contrastClass(swatch)}` : ""
                    }`}
                    onClick={() => {
                      setLive(null);
                      onChange(swatch.toLowerCase());
                      setIsOpen(false);
                    }}
                  >
                    {isActive && <i className="fa-solid fa-check" />}
                  </button>
                );
              })}
            </div>
          </div>
        </PopoverBody>
      </Popover>
    </>
  );
};
